import { CHATBOT_REFERENCE_IMAGE_MAX_BYTES } from "./constants";
import {
  COMPRESSED_IMAGE_TYPE,
  INITIAL_COMPRESSION_QUALITY,
  MAX_REFERENCE_IMAGE_DIMENSION,
} from "./chatbotHook.constants";

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The selected image could not be read."));
    };
    image.src = objectUrl;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("The image could not be compressed."))),
      COMPRESSED_IMAGE_TYPE,
      quality
    );
  });

export const optimizeReferenceImage = async (file: File): Promise<File> => {
  if (file.size <= CHATBOT_REFERENCE_IMAGE_MAX_BYTES) return file;

  const image = await loadImage(file);
  const initialScale = Math.min(
    1,
    MAX_REFERENCE_IMAGE_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight)
  );
  let width = Math.max(1, Math.round(image.naturalWidth * initialScale));
  let height = Math.max(1, Math.round(image.naturalHeight * initialScale));
  let quality = INITIAL_COMPRESSION_QUALITY;

  for (let attempt = 0; attempt < 7; attempt += 1) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Image compression is not available in this browser.");

    context.drawImage(image, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, quality);
    if (blob.size <= CHATBOT_REFERENCE_IMAGE_MAX_BYTES) {
      const baseName = file.name.replace(/\.[^.]+$/, "") || "reference-image";
      return new File([blob], `${baseName}.webp`, {
        type: COMPRESSED_IMAGE_TYPE,
        lastModified: file.lastModified,
      });
    }

    width = Math.max(1, Math.round(width * 0.8));
    height = Math.max(1, Math.round(height * 0.8));
    quality = Math.max(0.55, quality - 0.07);
  }

  throw new Error("The image is too large to optimize for upload.");
};
